import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import WindowedSelect from 'react-windowed-select';
import moment from 'moment';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { FaSave, FaAngleLeft } from 'react-icons/fa';
import { IoIosArrowForward } from 'react-icons/io';
import LeaseApiService from '../../services/apiService';

const REACT_APP_REPORTS = process.env.REACT_APP_REPORTS_URL;
const EMPTY_DATE_VALUES = ['', '0', null, undefined, '01-Jan-1900'];
const CHEQUE_METHODS = ['CHEQUE', 'PDC'];
const LISTING_ROUTE = '/Lease/Management/Lease/Receipt/Listing';

const parseApiDate = (value) => {
    if (EMPTY_DATE_VALUES.includes(value)) return null;
    const parsed = moment(value, ['DD-MMM-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
    return parsed.isValid() ? parsed.toDate() : null;
};

const emptyToNull = (value) => {
    const trimmed = (value || '').trim();
    return trimmed === '' ? null : trimmed;
};

const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? '#F5F5F5' : null,
        color: '#000',
        ':hover': { backgroundColor: '#F5F5F5', color: '#000' },
    }),
};

const EditLeaseReceipt = () => {
    const { receiptNo } = useParams();
    const navigate = useNavigate();
    const apiService = new LeaseApiService();

    const [loadState, setLoadState] = useState('loading'); // loading | ready | notfound | error
    const [loadErrorMessage, setLoadErrorMessage] = useState('');
    const [header, setHeader] = useState(null);
    const [lines, setLines] = useState([]);
    const [additional, setAdditional] = useState(null);

    const [debitAccounts, setDebitAccounts] = useState([]);
    const [blockingMessage, setBlockingMessage] = useState(null);
    const [submitError, setSubmitError] = useState('');

    const [form, setForm] = useState(null);
    const [initialForm, setInitialForm] = useState(null);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
    const [transTab, setTransTab] = useState(true);
    const [leaseTab, setLeaseTab] = useState(true);
    const [receivablesTab, setReceivablesTab] = useState(true);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [receiptNo]);

    const loadData = async () => {
        setLoadState('loading');
        setBlockingMessage(null);

        const [receiptResult, dropdownResult] = await Promise.allSettled([
            apiService.getLeaseReceiptByNo(receiptNo),
            apiService.getApiParamater('RECEIPT_DROPDOWN', `?dropdownTypes=DebitAccounts`),
        ]);

        if (dropdownResult.status === 'fulfilled') {
            setDebitAccounts(dropdownResult.value?.data?.dropdowns?.DebitAccounts || []);
        }

        if (receiptResult.status !== 'fulfilled') {
            setLoadState('error');
            setLoadErrorMessage('Something went wrong while loading the receipt. Please try again.');
            return;
        }

        const res = receiptResult.value;
        const statusCode = res?.data?.statusCode ?? res?.status;

        if (statusCode === 404) {
            setLoadState('notfound');
            return;
        }
        if (statusCode !== 200) {
            setLoadState('error');
            setLoadErrorMessage(res?.data?.message || 'Something went wrong while loading the receipt.');
            return;
        }

        const data = res?.data?.result ?? res?.data?.data;
        const receiptHeader = data?.header || {};
        setHeader(receiptHeader);
        setLines(data?.lines || []);
        setAdditional(data?.additional || null);

        const initial = {
            bankAccount: receiptHeader?.bankAccount || '',
            bankAccountLabel: receiptHeader?.bankName || '',
            chequeNo: receiptHeader?.chequeNo || '',
            chequeDate: parseApiDate(receiptHeader?.chequeDate),
            drawnOnBank: receiptHeader?.drawnOnBank || '',
            refNo: receiptHeader?.refNo || '',
            narration: receiptHeader?.narration || '',
        };
        setForm(initial);
        setInitialForm(initial);

        let monthClosed = false;
        try {
            const params = new URLSearchParams();
            params.append('Action', 'CheckOperatingMonth');
            params.append('TaxDescription', receiptHeader?.paymentDate);
            const monthRes = await apiService.getApiParamater('LEASE_NO_DATA', `?${params.toString()}`);
            const monthData = monthRes?.data?.result?.data ?? monthRes?.data?.data;
            const record = Array.isArray(monthData) ? monthData[0] : monthData;
            monthClosed = record?.isClosed === true || record?.IsClosed === true;
        } catch (error) {
            console.error('Error checking operating month:', error);
        }

        setLoadState('ready');
        setBlockingMessage(evaluateBlockingRules(receiptHeader, monthClosed));
    };

    const evaluateBlockingRules = (receiptHeader, monthClosed) => {
        if (receiptHeader?.chequeCount > 1) {
            return 'This receipt has multiple cheques and cannot be edited.';
        }
        if (!receiptHeader?.tenantNo || receiptHeader?.tenantNo === '0') {
            return 'No tenant details found for this receipt.';
        }
        if (receiptHeader?.isInFinancialPeriod === false) {
            return 'Receipt date should be between financial period.';
        }
        if (monthClosed) {
            return 'Month is closed for transactions.';
        }
        return null;
    };

    const isCleared = header?.isChequeCleared === true;
    const isChequeMethod = CHEQUE_METHODS.includes((header?.paymentMethod || '').trim().toUpperCase());
    const readOnly = !!blockingMessage;
    const dirty = form && initialForm && JSON.stringify(form) !== JSON.stringify(initialForm);

    const handleFieldChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleAccountChange = (selectedOption) => {
        setForm((prev) => ({
            ...prev,
            bankAccount: selectedOption?.value?.value || '',
            bankAccountLabel: selectedOption?.value?.text || '',
        }));
        setErrors((prev) => ({ ...prev, bankAccount: undefined }));
    };

    const validateField = (field, values = form) => {
        switch (field) {
            case 'bankAccount': {
                const value = (values.bankAccount || '').trim();
                if (!value || value === '0') return 'Select Debit Account.';
                return undefined;
            }
            case 'chequeNo': {
                if (!isChequeMethod) return undefined;
                const value = (values.chequeNo || '').trim();
                if (!value || value === '0') return 'Enter Cheque No.';
                if (value.length > 50) return 'Cheque No cannot exceed 50 characters.';
                return undefined;
            }
            case 'chequeDate': {
                if (!isChequeMethod) return undefined;
                if (!values.chequeDate) return 'Enter Cheque Date.';
                if (moment(values.chequeDate).isSameOrBefore(moment('2000-01-01'))) return 'Enter Cheque Date.';
                return undefined;
            }
            case 'drawnOnBank': {
                if (!isChequeMethod) return undefined;
                const value = (values.drawnOnBank || '').trim();
                if (!value || value === '0') return 'Enter Drawn On Bank.';
                if (value.length > 255) return 'Drawn On Bank cannot exceed 255 characters.';
                return undefined;
            }
            case 'refNo': {
                const value = (values.refNo || '').trim();
                if (value.length > 50) return 'Ref No cannot exceed 50 characters.';
                return undefined;
            }
            case 'narration': {
                const value = (values.narration || '').trim();
                if (value.length > 1000) return 'Narration cannot exceed 1000 characters.';
                return undefined;
            }
            default:
                return undefined;
        }
    };

    const validateForm = () => {
        const trimmed = {
            ...form,
            chequeNo: (form.chequeNo || '').trim(),
            drawnOnBank: (form.drawnOnBank || '').trim(),
            refNo: (form.refNo || '').trim(),
            narration: (form.narration || '').trim(),
        };
        const fields = ['bankAccount', 'chequeNo', 'chequeDate', 'drawnOnBank', 'refNo', 'narration'];
        const newErrors = {};
        fields.forEach((field) => {
            const message = validateField(field, trimmed);
            if (message) newErrors[field] = message;
        });
        setForm(trimmed);
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleBlur = (field) => {
        const message = validateField(field, form);
        setErrors((prev) => ({ ...prev, [field]: message }));
    };

    const handleSaveClick = () => {
        if (!validateForm()) return;
        setShowConfirm(true);
    };

    const buildSubmitBody = () => ({
        bankAccount: emptyToNull(form.bankAccount),
        chequeNo: isChequeMethod ? emptyToNull(form.chequeNo) : null,
        chequeDate: isChequeMethod && form.chequeDate ? moment(form.chequeDate).format('YYYY-MM-DD') : null,
        drawnOnBank: isChequeMethod ? emptyToNull(form.drawnOnBank) : null,
        refNo: emptyToNull(form.refNo),
        narration: emptyToNull(form.narration),
    });

    const handlePrint = () => {
        const token = localStorage.getItem('Token');
        const DB = localStorage.getItem('DataBase');
        if (!token) return;
        const popupWidth = 1000;
        const popupHeight = 600;
        const popupLeft = (window.screen.width - popupWidth) / 2;
        const popupTop = (window.screen.height - popupHeight) / 2;
        const popup = window.open(
            '',
            'ReportPopup',
            `width=${popupWidth},height=${popupHeight},left=${popupLeft},top=${popupTop},resizable=yes,scrollbars=yes`
        );
        if (!popup) return;
        const formEl = document.createElement('form');
        formEl.method = 'POST';
        formEl.action = REACT_APP_REPORTS;
        formEl.target = 'ReportPopup';
        const inputs = [
            { name: 'authToken', value: token },
            { name: '_db', value: DB },
            { name: 'RecordId', value: '' },
            { name: 'LeaseNo', value: header?.leaseNo },
            { name: 'ReceiptNo', value: receiptNo },
            { name: 'reportName', value: 'LEASEBULKRPT' },
        ];
        inputs.forEach(({ name, value }) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            formEl.appendChild(input);
        });
        document.body.appendChild(formEl);
        formEl.submit();
        document.body.removeChild(formEl);
    };

    const handleConfirmSave = async () => {
        setSaving(true);
        setSubmitError('');
        try {
            const body = buildSubmitBody();
            const res = await apiService.updateLeaseReceipt(receiptNo, body);
            const statusCode = res?.data?.statusCode ?? res?.status;

            if (statusCode === 200) {
                const savedReceiptNo = res?.data?.result?.receiptNo ?? res?.data?.data?.receiptNo ?? receiptNo;
                toast.success(
                    <div>
                        <div>{res?.data?.message || `Lease Receipt ${savedReceiptNo} updated successfully`}</div>
                        <button type="button" className="mainbtn mt-2" onClick={handlePrint}>
                            Print
                        </button>
                    </div>
                );
                setShowConfirm(false);
                navigate(LISTING_ROUTE);
                return;
            }

            if (statusCode === 400) {
                setSubmitError(res?.data?.message || 'Please correct the highlighted errors and try again.');
            } else if (statusCode === 404) {
                toast.error("Receipt not found or you don't have access to it.");
                navigate(LISTING_ROUTE);
                return;
            } else if (statusCode === 409) {
                toast.error(res?.data?.message || 'This receipt could not be updated.');
            } else {
                toast.error('Something went wrong while updating the receipt. Please try again.');
            }
        } catch (error) {
            console.error('Error updating lease receipt:', error);
            toast.error('Something went wrong while updating the receipt. Please try again.');
        } finally {
            setSaving(false);
            setShowConfirm(false);
        }
    };

    const handleRequestClose = () => {
        if (dirty) {
            setShowDiscardConfirm(true);
        } else {
            navigate(LISTING_ROUTE);
        }
    };

    const formatAmount = (value) => (typeof value === 'number' ? value.toFixed(3) : value);
    const formatDisplayDate = (value) => {
        const parsed = parseApiDate(value);
        return parsed ? moment(parsed).format('DD-MMM-YYYY') : '-';
    };

    return (
        <div className="p-3">
            <div className='d-flex justify-content-between border-box2 p-3' style={{ borderRadius: "8px" }}>
                <h6 className='h6'>Edit Lease Receipt {receiptNo}</h6>
                <div className='d-flex justify-content-end gap-1'>
                    {loadState === 'ready' && !blockingMessage && (
                        saving ?
                            <button className='mainbtn' type="button"><ClipLoader color="#ffffff" size={20} /></button>
                            :
                            <button className='mainbtn' onClick={handleSaveClick} type="button" disabled={!dirty}><FaSave className='center mt-1 mx-2' /> <div>Save</div></button>
                    )}
                    <button onClick={handleRequestClose} className='mainbtnSecondary' disabled={saving}><FaAngleLeft className='center mt-1 mx-2' /><div>Back</div></button>
                </div>
            </div>

            {loadState === 'loading' && (
                <div className="text-center p-5">
                    <ClipLoader color="#6c5ce7" size={36} />
                </div>
            )}

            {loadState === 'notfound' && (
                <div className="border-box2 p-4 mt-3 text-center">
                    <p>Receipt not found or you don't have access to it.</p>
                    <button type="button" className="mainbtn" onClick={() => navigate(LISTING_ROUTE)}>Close</button>
                </div>
            )}

            {loadState === 'error' && (
                <div className="border-box2 p-4 mt-3 text-center">
                    <p className="text-danger">{loadErrorMessage}</p>
                    <button type="button" className="mainbtn" onClick={loadData}>Retry</button>
                </div>
            )}

            {loadState === 'ready' && header && (
                <>
                    {submitError && (
                        <div className="alert alert-danger mt-3 mb-0">{submitError}</div>
                    )}
                    {blockingMessage && (
                        <div className="alert alert-warning mt-3 mb-0">{blockingMessage}</div>
                    )}
                    {!blockingMessage && isCleared && (
                        <div className="alert alert-info mt-3 mb-0">
                            This PDC is already cleared. Cheque details and debit account are locked.
                        </div>
                    )}

                    {/* Transaction Details */}
                    <div className="cost-center-section mt-3">
                        <div className="d-flex cursor-pointer slide-header" onClick={() => setTransTab(!transTab)}>
                            <div className='param-label pt-1 col-2'>Transaction Details</div>
                            <IoIosArrowForward size={30} className={`arrow fade-rotate ${transTab ? "open" : ""}`} />
                        </div>
                        <div className={`slide-content ${transTab ? 'open' : 'closed'}`}>
                            <div className={`border-box2 p-3 ${transTab ? 'slide-in' : 'slide-out'}`}>
                                <div className="form-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' }}>
                                    <div className="d-flex">
                                        <label className="form-label pt-1 start">Receipt No</label>
                                        <input type="text" className="p-2 form-control w-75" value={header.receiptNo || ''} disabled />
                                    </div>
                                    <div className="d-flex">
                                        <label className="form-label pt-1 start">Payment Method</label>
                                        <input type="text" className="p-2 form-control w-75" value={header.paymentMethod || ''} disabled />
                                    </div>
                                    <div className="d-flex">
                                        <label className="form-label pt-1 start">Receipt Type</label>
                                        <input type="text" className="p-2 form-control w-75" value={header.receiptType || ''} disabled />
                                    </div>
                                    <div className="d-flex">
                                        <label className="form-label pt-1 start">Receipt Date</label>
                                        <input type="text" className="p-2 form-control w-75" value={formatDisplayDate(header.paymentDate)} disabled />
                                    </div>
                                    <div className="d-flex">
                                        <label className="form-label pt-1 start">Receipt Amount</label>
                                        <input type="text" className="p-2 form-control w-75" value={formatAmount(header.receiptAmount)} disabled />
                                    </div>
                                    <div>
                                        <div className="d-flex">
                                            <label className="form-label pt-1 start">Cheque / Ref No</label>
                                            <input
                                                type="text"
                                                className="p-2 form-control w-75"
                                                maxLength={50}
                                                disabled={readOnly || isCleared || !isChequeMethod}
                                                value={form.chequeNo}
                                                onChange={(e) => handleFieldChange('chequeNo', e.target.value)}
                                                onBlur={() => handleBlur('chequeNo')}
                                            />
                                        </div>
                                        {errors.chequeNo && <div className="text-danger small">{errors.chequeNo}</div>}
                                    </div>
                                    <div>
                                        <div className="d-flex">
                                            <label className="form-label pt-1 start">Cheque / Ref Date</label>
                                            <DatePicker
                                                selected={form.chequeDate}
                                                onChange={(date) => handleFieldChange('chequeDate', date)}
                                                onBlur={() => handleBlur('chequeDate')}
                                                dateFormat="dd-MMM-yyyy"
                                                className="p-2 form-control w-100"
                                                disabled={readOnly || isCleared || !isChequeMethod}
                                            />
                                        </div>
                                        {errors.chequeDate && <div className="text-danger small">{errors.chequeDate}</div>}
                                    </div>
                                    <div>
                                        <div className="d-flex">
                                            <label className="form-label pt-1 start">Drawn On Bank</label>
                                            <input
                                                type="text"
                                                className="p-2 form-control w-75"
                                                maxLength={255}
                                                disabled={readOnly || isCleared || !isChequeMethod}
                                                value={form.drawnOnBank}
                                                onChange={(e) => handleFieldChange('drawnOnBank', e.target.value)}
                                                onBlur={() => handleBlur('drawnOnBank')}
                                            />
                                        </div>
                                        {errors.drawnOnBank && <div className="text-danger small">{errors.drawnOnBank}</div>}
                                    </div>
                                    <div>
                                        <div className="d-flex align-items-center">
                                            <label className="form-label pt-1 start">Account (Dr)</label>
                                            <WindowedSelect
                                                className="w-75 p-0"
                                                theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                                menuPortalTarget={document.body}
                                                menuPlacement="auto"
                                                menuShouldScrollIntoView={false}
                                                styles={selectStyles}
                                                isDisabled={true}
                                                value={form.bankAccount ? { label: form.bankAccountLabel, value: { value: form.bankAccount, text: form.bankAccountLabel } } : null}
                                                onChange={handleAccountChange}
                                                onBlur={() => handleBlur('bankAccount')}
                                                options={debitAccounts?.map((rec) => ({ value: rec, label: rec?.text }))}
                                            />
                                            {form.bankAccount && <span className="text-danger small ms-2">{form.bankAccount}</span>}
                                        </div>
                                        {errors.bankAccount && <div className="text-danger small">{errors.bankAccount}</div>}
                                    </div>
                                    <div>
                                        <div className="d-flex">
                                            <label className="form-label pt-1 start">Ref No</label>
                                            <input
                                                type="text"
                                                className="p-2 form-control w-75"
                                                maxLength={50}
                                                disabled={readOnly}
                                                value={form.refNo}
                                                onChange={(e) => handleFieldChange('refNo', e.target.value)}
                                                onBlur={() => handleBlur('refNo')}
                                            />
                                        </div>
                                        {errors.refNo && <div className="text-danger small">{errors.refNo}</div>}
                                    </div>
                                    <div>
                                        <div className="d-flex">
                                            <label className="form-label pt-1 start">Narration</label>
                                            <textarea
                                                className="p-2 form-control w-75"
                                                maxLength={1000}
                                                disabled={readOnly}
                                                value={form.narration}
                                                onChange={(e) => handleFieldChange('narration', e.target.value)}
                                                onBlur={() => handleBlur('narration')}
                                            />
                                        </div>
                                        {errors.narration && <div className="text-danger small">{errors.narration}</div>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Lease Details */}
                    <div className="cost-center-section mt-3">
                        <div className="d-flex cursor-pointer slide-header" onClick={() => setLeaseTab(!leaseTab)}>
                            <div className='param-label pt-1 col-2'>Lease Details</div>
                            <IoIosArrowForward size={30} className={`arrow fade-rotate ${leaseTab ? "open" : ""}`} />
                        </div>
                        <div className={`slide-content ${leaseTab ? 'open' : 'closed'}`}>
                            <div className={`border-box2 p-3 ${leaseTab ? 'slide-in' : 'slide-out'}`}>
                                <div className="form-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' }}>
                                    <div className="d-flex">
                                        <label className="form-label pt-1 start">Lease No</label>
                                        <input type="text" className="p-2 form-control w-75" value={header.leaseNo || ''} disabled />
                                    </div>
                                    <div className="d-flex">
                                        <label className="form-label pt-1 start">Lease Status</label>
                                        <input type="text" className="p-2 form-control w-75" value={header.leaseStatus || ''} disabled />
                                    </div>
                                    <div className="d-flex">
                                        <label className="form-label pt-1 start">Property</label>
                                        <input type="text" className="p-2 form-control w-75" value={header.property_Name || ''} disabled />
                                    </div>
                                    <div className="d-flex">
                                        <label className="form-label pt-1 start">Unit No</label>
                                        <input type="text" className="p-2 form-control w-75" value={header.unit_No || ''} disabled />
                                    </div>
                                    <div className="d-flex">
                                        <label className="form-label pt-1 start">Tenant No</label>
                                        <input type="text" className="p-2 form-control w-75" value={header.tenantNo || ''} disabled />
                                    </div>
                                    <div className="d-flex">
                                        <label className="form-label pt-1 start">Tenant Name</label>
                                        <input type="text" className="p-2 form-control w-75" value={header.tenantName || ''} disabled />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Receivables */}
                    <div className="cost-center-section mt-3">
                        <div className="d-flex cursor-pointer slide-header" onClick={() => setReceivablesTab(!receivablesTab)}>
                            <div className='param-label pt-1 col-2'>Receivables</div>
                            <IoIosArrowForward size={30} className={`arrow fade-rotate ${receivablesTab ? "open" : ""}`} />
                        </div>
                        <div className={`slide-content ${receivablesTab ? 'open' : 'closed'}`}>
                            <div className={`border-box2 ${receivablesTab ? 'slide-in' : 'slide-out'}`}>
                                <div className='table-container' style={{ overflowX: 'auto', height: "auto" }}>
                                    <table className='table table-bordered'>
                                        <thead className='dynoTable'>
                                            <tr>
                                                <th>Description</th>
                                                <th>Due Date</th>
                                                <th>Invoice No</th>
                                                <th>Net Amount</th>
                                                <th>Amount</th>
                                                <th>Credit Account</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {lines.map((line) => (
                                                <tr key={line.recordId}>
                                                    <td className='m-0 p-0'><input value={line.description || ''} className='p-1' disabled style={{ width: "auto" }} /></td>
                                                    <td className='m-0 p-0'><input value={formatDisplayDate(line.leaseDueDate)} className='p-1' disabled style={{ width: "auto" }} /></td>
                                                    <td className='m-0 p-0'><input value={line.invoiceNo || ''} className='p-1' disabled style={{ width: "auto" }} /></td>
                                                    <td className='m-0 p-0'><input value={formatAmount(line.netAmount)} className='p-1' disabled style={{ width: "auto" }} /></td>
                                                    <td className='m-0 p-0'><input value={formatAmount(line.amount)} className='p-1' disabled style={{ width: "auto" }} /></td>
                                                    <td className='m-0 p-0'><input value={line.crAcName || ''} className='p-1' disabled style={{ width: "auto" }} /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan={3}><strong>Total</strong></td>
                                                <td><strong>{formatAmount(lines.reduce((sum, l) => sum + (l.netAmount || 0), 0))}</strong></td>
                                                <td><strong>{formatAmount(lines.reduce((sum, l) => sum + (l.amount || 0), 0))}</strong></td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                                {additional && (
                                    <div className="p-3">Additional Amount: {formatAmount(additional.additionalAmount)} - {additional.additionalAccountName}</div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}

            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} backdrop="static" size="sm">
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Update</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to update receipt {receiptNo}?
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="mainbtnGry" onClick={() => setShowConfirm(false)} disabled={saving}>No</button>
                    <button type="button" className="mainbtnRed center" onClick={handleConfirmSave} disabled={saving}>
                        {saving ? <ClipLoader color="#ffffff" size={18} /> : 'Yes'}
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal show={showDiscardConfirm} onHide={() => setShowDiscardConfirm(false)} backdrop="static" size="sm">
                <Modal.Header closeButton>
                    <Modal.Title>Discard changes?</Modal.Title>
                </Modal.Header>
                <Modal.Body>You have unsaved changes. Are you sure you want to discard them?</Modal.Body>
                <Modal.Footer>
                    <button type="button" className="mainbtnGry" onClick={() => setShowDiscardConfirm(false)}>No</button>
                    <button type="button" className="mainbtnRed" onClick={() => navigate(LISTING_ROUTE)}>Yes, discard</button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default EditLeaseReceipt;
